import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EnvironmentService } from '../../../../../../common/services/environment-service';
import { ApplicationDTO } from '../../../../../models/application/ApplicationDTO';
import { convertToUTC, DateUtil } from '../../../../../../common/util/DateUtil';
import { MatDialog } from '@angular/material/dialog';
import { NameValueDTO } from '../../../../../../common/models/NameValueDTO';
import { debounceTime, finalize, take, takeUntil } from 'rxjs/operators';
import { LookupService } from '../../../../../../common/services/lookup.service';
import { LookupValue } from '../../../../../../clm/types/lookup-value.enum';
import { GenericListDialogComponent } from '../../../../../../common/dialogs/generic-list-dialog/generic-list-dialog.component';
import { GenericReasoningDialogComponent } from '../../../../../../common/components/dialogs/generic-reasoning-dialog/generic-reasoning-dialog.component';
import { OnboardingAdminPortalService } from '../../../../../services/onboard-application.service';
import { Common, deepCopy } from '../../../../../../common/util/Util';
import { ApplicationPersonDTO } from '../../../../../models/application/ApplicationPersonDTO';
import { RuleMessageDialogComponent } from '../../../../dialogs/rule-message-dialog/rule-message-dialog.component';
import {
  ApplicationRuleMessageDTO,
  RuleMessageDialogData,
} from '../../../../../models/application/ApplicationRuleMessageDTO';
import { SharedService } from '../../../../../../common/services/shared-service';
import { UserAuthorisationService } from 'src/app/modules/common/services/user-authorisation.service';
import { NameValueCodeDTO } from '../../../../../../common/models/NameValueCodeDTO';
import { ApplicationIndicatorComponent } from '../../../../dialogs/application-indicator/application-indicator.component';
import { ApplicationStatusDTO } from '../../../../../models/application/ApplicationStatusDTO';
import { ConfirmationDialogComponent } from '../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ApplicationUtilityService } from '../../../../../services/application-utility.service';
import { DisplayStrings } from '../../../../../../common/constants/DisplayStrings';
import { Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PolicyholderGroupDTO } from '../../../../../../ent/models/PolicyholderGroupDTO';
import { PolicyholderGroupService } from '../../../../../../ent/services/policyholder-group.service';
import { PagedRequestDTO } from '../../../../../../common/models/PagedRequestDTO';
import { BrokerService } from '../../../../../services/broker.service';
import { BrokerDTO } from '../../../../../models/broker/BrokerDTO';
import { PagedResponseDTO } from '../../../../../../common/models/PagedResponseDTO';
import { ApplicationUtil } from 'src/app/modules/obd/obd-common/utils/ApplicationUtil';
import { StartDateOption } from 'src/app/modules/obd/obd-common/components/obd-bulk-application-import/obd-bulk-application-import.component';

const ALL_STATUSES = 'All Statuses';

@Component({
  selector: 'greydom-application-summary',
  templateUrl: './application-summary.component.html',
  styleUrls: ['./application-summary.component.scss'],
})
export class ApplicationSummaryComponent
  implements OnChanges, OnInit, OnDestroy
{
  private readonly STATUS_NOT_TAKEN_UP = 'Not Taken Up';
  private readonly STATUS_ACCEPTED = 'Accepted';
  readonly destroyed$ = new Subject();
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly dateFormat = DateUtil.dateFormat;

  @Input() application: ApplicationDTO;
  @Input() userFirstLastName = '';
  @Input() diagnosisGroups: NameValueCodeDTO[];
  @Output() dispatchFetchApplicationRequest: EventEmitter<boolean> = new EventEmitter<boolean>();

  status = ALL_STATUSES; // Application status
  statuses: NameValueDTO[]; // Application statuses
  isProcessing = false;
  hasReprocessPermissions = false;
  isApplicationLocked = false;
  originalStatusId: number;
  originalStageId: number;

  editStartDate = false;
  startDateOptions: StartDateOption[] = [];
  startDateFormControl: FormControl = new FormControl();
  isStartDateSave: boolean;

  // Inline Edit PHG
  editEmployerGroup = false;
  employerGroupFormControl: FormControl<string> = new FormControl();
  employerGroupTextChanged: Subject<string> = new Subject<string>();
  filteredEmployerGroups: PolicyholderGroupDTO[] = [];
  selectedEmployerGroup: PolicyholderGroupDTO;

  // Inline Edit Broker
  editBroker = false;
  brokerFormControl: FormControl<string> = new FormControl();
  brokerTextChanged: Subject<string> = new Subject<string>();
  filteredBrokers: BrokerDTO[] = [];
  selectedBroker: BrokerDTO;

  selectedOption: any;
  hasPermissions = false;

  constructor(
    private dialog: MatDialog,
    private sharedService: SharedService,
    private lookupService: LookupService,
    private applicationService: OnboardingAdminPortalService,
    private userAuthorisationService: UserAuthorisationService,
    private applicationUtilityService: ApplicationUtilityService,
    private policyholderGroupService: PolicyholderGroupService,
    private brokerService: BrokerService,
    private environmentService: EnvironmentService
  ) {}

  get isAuthorisedToReprocess(): boolean {
    return (
      !this.isProcessing &&
      this.hasReprocessPermissions &&
      !this.isApplicationLocked
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.application && changes.application.currentValue) {
      this.status = this.application.status?.statusName || ALL_STATUSES;
    }
  }

  ngOnInit() {
    this.initSubscriptions();
    this.getListOfLookupValues(LookupValue.ApplicationStatus);
    this.hasReprocessPermissions = this.doesUserHaveReprocessPermissions(); // This flag applies for certain updates e.g Reprocess/Waiting periods and for team leaders with higher privileges
    this.isApplicationLocked =
      this.applicationUtilityService.isApplicationLocked(
        this.application?.status?.applicationStatusId,
        this.application?.stage?.applicationStageId
      );
    this.originalStageId = this.application?.stage?.applicationStageId;
    this.originalStatusId = this.application?.status?.applicationStatusId;
    this.setPermission();
    this.generateStartDateOptions();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions(): void {
    this.employerGroupTextChanged
      .pipe(debounceTime(this.debounceTime), takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value) {
          this.searchEmployerGroups(value);
        }
      });

    this.brokerTextChanged
      .pipe(debounceTime(this.debounceTime), takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value) {
          this.searchBrokers(value);
        }
      });
  }

  private generateStartDateOptions(): void {
    this.startDateOptions = ApplicationUtil.generateStartDateOptions();

    // Check if backend start date is already in list
    const backendOption = this.formatDate(
      new Date(this.application.membership.startDate)
    );

    const exists = this.startDateOptions.some((opt) => opt.value === backendOption);
    if (!exists) {
      this.startDateOptions.push({
        value: backendOption,
        displayValue: backendOption,
        isCurrentMonth: false
      });
    }
    this.startDateOptions.sort((a, b) => {
      const dateA = a.value.split('/').map(Number);
      const dateB = b.value.split('/').map(Number);
      return (
        new Date(dateA[0], dateA[1] - 1, dateA[2]).getTime() -
        new Date(dateB[0], dateB[1] - 1, dateB[2]).getTime()
      );
    });
    this.startDateFormControl.setValue(backendOption);

    this.startDateFormControl.valueChanges.subscribe((selectedValue) => {
      this.selectedOption = selectedValue;
      this.isStartDateSaveDisabled();
    });
  }

  getStartDateDisplayValue(): string {
    return this.formatDate(new Date(this.application.membership.startDate));
  }

  // Using the same date formatting as add-insured-person-view
  private formatDate(date: Date): string {
    if (date) {
      return this.sharedService.formatDate(date, this.dateFormat);
    } else {
      return '';
    }
  }

  setPermission(): void {
    const hasRole =
      this.userAuthorisationService.hasOnBoardingQC() ||
      this.userAuthorisationService.hasRoleEnterpriseManager();
    this.hasPermissions = hasRole && !this.isApplicationLocked;
  }

  onClickOpenPremiumCalculationDialog() {
    const rows: string[] = [];

    // Columns
    rows.push(
      `Person;Nominal Premium;Penalty Premium;MSA Premium;Total Premium`
    );

    // Row 1: Columns
    rows.push(
      `Total;${this.formatValue(
        this.application.membership?.premium
      )};${this.formatValue(
        this.application.membership?.premiumPenalty
      )};${this.formatValue(
        this.application.membership?.premiumMsa
      )};${this.formatValue(this.application.membership?.premiumTotal)}`
    );

    // Row 2+: Per person
    this.application.membership?.persons?.forEach((person) => {
      rows.push(
        `${person.firstName} ${person.lastName};${this.formatValue(
          person.premium
        )};${this.formatValue(person.premiumPenalty)};${this.formatValue(
          person.premiumMsa
        )};${this.formatValue(person.premiumTotal)}`
      );
    });

    this.dialog.open(GenericListDialogComponent, {
      width: '40vw',
      data: {
        list: rows,
        heading: 'Premium Calculation',
      },
    });
  }

  getDisplayString(code: string | undefined, name: string | undefined): string {
    return code && name ? `${code}: ${name}` : 'N/A';
  }

  onClickOpenReport($event: MouseEvent, applicationId: number): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!applicationId) {
      return;
    }
    const dialogData: RuleMessageDialogData = {
      application: this.application,
      status: this.application.status?.statusName || '',
      stage: this.application.stage?.stageName || '',
      showDownloadButton: false,
    };

    this.dialog.open(RuleMessageDialogComponent, {
      width: '80vw',
      height: '80vh',
      data: dialogData,
    });
  }

  sendCommunicationPack() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: 'Are you sure you want to send the communication pack? Please ensure that all previous emails and SMSes have been reset on the Emails tab. Once confirmed, click YES to proceed.',
    });

    dialogRef.afterClosed().subscribe((isConfirmed) => {
      if (isConfirmed) {
        this.applicationService.sendWelcomePack(this.application).subscribe(
          () => {
            this.sharedService.openSnackBar(
              `Check the Emails tab to confirm that the communication pack was sent.`,
              'Dismiss'
            );
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
      }
    });
  }

  canSendCommunicationPack(): boolean {
    const hasRolesForCommunicationPackSending = this.userAuthorisationService.hasOnBoardingManager() || this.userAuthorisationService.hasRoleEnterpriseManager();
    return (
      this.environmentService.getWelcomePackSendEnabled() &&
      hasRolesForCommunicationPackSending &&
      this.originalStageId >= 6 && this.originalStatusId == 3
    );
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService
      .getListOfLookupValues(entity)
      .pipe(take(1))
      .subscribe({
        next: (response: NameValueDTO[]) => {
          this.statuses = this.updateLookupWithDefault(response, ALL_STATUSES);
        },
        error: (error) => {
          console.error(
            'Error getting application status lookup values',
            error
          );
        },
      });
  }

  isStartDateSaveDisabled(): void {
    if (
      this.selectedOption ==
      this.formatDate(new Date(this.application.membership.startDate))
    ) {
      this.isStartDateSave = true;
    } else {
      this.isStartDateSave = false;
    }
  }

  // Handle application status selection
  statusSelected(value: string): void {
    if (value !== this.status && value !== ALL_STATUSES) {
      this.openReasoningDialog(value);
    }
  }

  onClickReprocessApplication($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.isAuthorisedToReprocess) {
      return; // Prevent multiple clicks + Unauthorized users + Application is locked to reprocess application
    }

    this.processApplication({
      onSuccess: (response: ApplicationDTO) => {
        this.application = response;
        this.sharedService.openSnackBar(`Application reprocessed`, 'Dismiss');

        if (this.application.applicationId) {
          this.onClickOpenReport($event, this.application.applicationId);
          this.dispatchFetchApplicationRequest.emit(true);
        }
      },
    });
  }

  handleUpdateRequest(reFetchApplication = false): void {
    if (reFetchApplication && this.application && !this.isProcessing) {
      this.updateApplication({
        onSuccess: (response: ApplicationDTO) => {
          this.application = response;
          this.sharedService.openSnackBar(
            'Application updated successfully',
            'Dismiss'
          );
        },
      });
    }
  }

  private updateApplication(options: {
    onSuccess: (response: ApplicationDTO) => void;
  }): void {
    this.isProcessing = true;
    const payload: ApplicationDTO = this.getApplicationDTOPayload();

    this.applicationService
      .updateApplication(payload)
      .pipe(
        take(1),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe({
        next: (response: ApplicationDTO) => {
          options.onSuccess(response);
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(
            error?.businessMessage || 'Failed to update application'
          );
        },
      });
  }

  private processApplication(options: {
    onSuccess: (response: ApplicationDTO) => void;
  }): void {
    this.isProcessing = true;
    const payload: ApplicationDTO = this.getApplicationDTOPayload();

    this.applicationService
      .processApplication(payload)
      .pipe(
        take(1),
        finalize(() => (this.isProcessing = false))
      )
      .subscribe({
        next: (response: ApplicationDTO) => {
          options.onSuccess(response);
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(
            error?.businessMessage || 'Failed to process application'
          );
        },
      });
  }

  reprocessingTooltip(): string {
    const permissionMessage =
      DisplayStrings.permissionApplicationMessages?.rolePermissionReprocess;
    const statusMessage =
      DisplayStrings.permissionApplicationMessages?.statusReprocess;
    return this.isApplicationLocked ? statusMessage : permissionMessage;
  }

  onPersonsUpdated(updatedPersons: ApplicationPersonDTO[]): void {
    if (this.application && this.application.membership) {
      this.application.membership = {
        ...this.application.membership,
        persons: updatedPersons,
      };
    }
  }

  formatValue(value: number): string {
    return value ? value.toFixed(2) : '0.00';
  }

  private getApplicationDTOPayload(): ApplicationDTO {
    const payload: ApplicationDTO = deepCopy(this.application);

    // Update Waiting Period Fields
    if (payload.membership && payload.membership.persons) {
      payload.membership.persons = payload.membership.persons.map(
        (person: ApplicationPersonDTO) => {
          const editedPerson: Partial<ApplicationPersonDTO> = {};
          if (person.overrideGeneralWaitingPeriod) {
            editedPerson.generalWaitingPeriod = person.generalWaitingPeriod;
            editedPerson.overrideGeneralWaitingPeriod = true;
          }
          if (person.overridePreExistingConditionWaitingPeriod) {
            editedPerson.preExistingConditionWaitingPeriod =
              person.preExistingConditionWaitingPeriod;
            editedPerson.overridePreExistingConditionWaitingPeriod = true;
          }
          return Object.keys(editedPerson).length > 0
            ? { ...person, ...editedPerson }
            : person;
        }
      );
    }
    // Update Audit fields
    payload.updated = new Date();
    payload.updatedBy = this.userFirstLastName;
    return payload;
  }

  openReasoningDialog(newStatus: string): void {
    if (newStatus == this.STATUS_NOT_TAKEN_UP) {
      this.openApplicationIndicatorDialog();
      return;
    }

    // Find first rule message with a non-empty justification
    const reasonOverrideJustification =
      this.application.ruleMessages?.find(
        (message: ApplicationRuleMessageDTO) =>
          message?.overrideJustification?.trim()
      )?.overrideJustification || '';

    const dialogRef = this.dialog.open(GenericReasoningDialogComponent, {
      width: '40vw',
      data: {
        heading: 'Reason for Status Change',
        reason: reasonOverrideJustification,
      },
    });

    dialogRef.afterClosed().subscribe((reason) => {
      if (reason) {
        this.updateApplicationStatus(newStatus, reason);
      } else {
        // Reset the status dropdown if the user cancels
        this.status = this.application.status?.statusName || ALL_STATUSES;
      }
    });
  }

  private openApplicationIndicatorDialog() {
    const dialogRef = this.dialog.open(ApplicationIndicatorComponent, {
      width: '40vw',
      data: {
        applicationId: this.application.applicationId,
        selectedIndicators: this.application.indicators,
        createdBy: this.userFirstLastName,
      },
    });
    dialogRef.afterClosed().subscribe((indicators) => {
      if (indicators) {
        this.updateApplicationStatus(this.STATUS_NOT_TAKEN_UP, null);
        this.application.indicators = indicators;
      } else {
        // Reset the status dropdown if the user cancels
        this.status = this.application.status?.statusName || ALL_STATUSES;
      }
    });
  }

  private doesUserHaveReprocessPermissions(): boolean {
    return (
      this.userAuthorisationService.hasRoleOperationsManager() ||
      this.userAuthorisationService.hasOnBoardingTeamLeader()
    );
  }

  private updateApplicationStatus(newStatus: string, reason: string): void {
    this.status = newStatus;

    const selectedStatus: NameValueDTO = this.statuses.find(
      (status) => status.name === newStatus
    );

    if (selectedStatus) {
      if (this.application.status == null) {
        this.application.status = new ApplicationStatusDTO();
      }
      this.application.status.applicationStatusId = selectedStatus.value;
      this.application.status.statusName = selectedStatus.name;

      if (this.application.manualStatus == null) {
        this.application.manualStatus = new ApplicationStatusDTO();
      }
      this.application.manualStatus.applicationStatusId = selectedStatus.value;
      this.application.manualStatus.statusName = selectedStatus.name;
    }

    if (this.application.ruleMessages?.length) {
      this.application.ruleMessages = this.application.ruleMessages.map(
        (message: ApplicationRuleMessageDTO) => {
          return {
            ...message,
            overrideIndicator:
              newStatus === this.STATUS_ACCEPTED ? true : false, // Critical making sure ONLY 'Accepted' status has overrideIndicator set to true
            overrideJustification: reason || '',
            updated: new Date(),
            updatedBy: this.userFirstLastName,
          };
        }
      );
    }
  }

  private updateLookupWithDefault(
    response: NameValueDTO[] | null,
    defaultName: string
  ): NameValueDTO[] {
    const processedArray: NameValueDTO[] = [...(response || [])];
    processedArray.unshift({ value: 0, name: defaultName });
    return processedArray;
  }

  overrideEmployerGroupClicked(): void {
    this.editEmployerGroup = true;
    const code = this.application?.employerGroup?.employerGroupCode;
    const name = this.application?.employerGroup?.employerGroupName;
    this.employerGroupFormControl.setValue(this.getDisplayString(code, name));
  }

  onInputChangedSearchEmployerGroups($event): void {
    this.employerGroupTextChanged.next($event.target.value);
  }

  searchEmployerGroups(searchValue: string): void {
    const pagedRequestDTO: PagedRequestDTO = {
      entityClass: 'TblPolicyholderGroup',
      pageSize: 10,
      pageNumber: 0,
      sortField: 'name',
      sortDirection: 'ASC',
      fieldMap: {},
    };

    const fields = [
      { key: 'name', name: 'name', logicGate: 'AND', bracket: '(' },
      {
        key: 'policyholderGroupNumber',
        name: 'policyholderGroupNumber',
        logicGate: 'OR',
        bracket: ')',
      },
    ];

    fields.forEach((field) => {
      pagedRequestDTO.fieldMap[field.key] = {
        name: field.name,
        value: searchValue,
        logicGate: field.logicGate,
        bracket: field.bracket || '',
      };
    });

    this.policyholderGroupService
      .getListOfPolicyholderGroups(pagedRequestDTO)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.filteredEmployerGroups = response.resultList;
        },
        error: (error) =>
          console.error('Error fetching employer groups:', error),
      });
  }

  onOptionSelected(option: any): void {
    this.selectedOption = option;
  }

  onSave(): void {
    if (!this.selectedOption) {
      return;
    }

    if (this.editEmployerGroup) {
      this.saveEmployerGroup(this.selectedOption);
    } else if (this.editBroker) {
      this.saveBroker(this.selectedOption);
    } else if (this.editStartDate) {
      this.saveStartDate(this.selectedOption);
    }
  }

  private saveEmployerGroup(employerGroup: PolicyholderGroupDTO): void {
    this.selectedEmployerGroup = employerGroup;
    this.application.employerGroup = {
      applicationEmployerGroupId:
        this.application.employerGroup?.applicationEmployerGroupId,
      employerGroupCode: employerGroup.policyholderGroupNumber,
      employerGroupName: employerGroup.name,
    };
    this.editEmployerGroup = false;
    this.selectedOption = null;
    this.updateApplication({
      onSuccess: (response: ApplicationDTO) => {
        this.application = response;
        this.sharedService.openSnackBar(
          'Employer group updated successfully',
          'Dismiss'
        );
      },
    });
  }

  private saveBroker(broker: BrokerDTO): void {
    this.selectedBroker = broker;
    this.application.intermediatory = {
      applicationIntermediatoryId:
        this.application.intermediatory?.applicationIntermediatoryId,
      intermediatoryCode: broker.brokerNumber,
      intermediatoryName: broker.name,
    };
    this.editBroker = false;
    this.selectedOption = null;
    this.updateApplication({
      onSuccess: (response: ApplicationDTO) => {
        this.application = response;
        this.sharedService.openSnackBar(
          'Broker updated successfully',
          'Dismiss'
        );
      },
    });
  }

  private saveStartDate(date: string): void {
    this.application.membership.startDate = convertToUTC(date);
    this.startDateFormControl.setValue(date);
    this.editStartDate = false;
    this.selectedOption = null;
    this.updateApplication({
      onSuccess: (response: ApplicationDTO) => {
        this.application = response;
        this.sharedService.openSnackBar(
          'Start date updated successfully',
          'Dismiss'
        );
      },
    });
  }

  clearEmployerGroupEdit(): void {
    this.editEmployerGroup = false;
    this.employerGroupFormControl.setValue('');
    this.selectedOption = null;
  }

  clearBrokerEdit(): void {
    this.editBroker = false;
    this.brokerFormControl.setValue('');
    this.selectedOption = null;
  }

  clearSaveDateEdit(): void {
    this.editStartDate = false;
    const originalStartDate = this.formatDate(
      new Date(this.application.membership.startDate)
    );
    this.startDateFormControl.setValue(originalStartDate);
    this.selectedOption = null;
  }

  overrideBrokerClicked(): void {
    this.editBroker = true;
    const code = this.application?.intermediatory?.intermediatoryCode;
    const name = this.application?.intermediatory?.intermediatoryName;
    this.brokerFormControl.setValue(this.getDisplayString(code, name));
  }

  overrideStartDateClicked(): void {
    this.editStartDate = true;
  }

  onInputChangedSearchBrokers($event): void {
    this.brokerTextChanged.next($event.target.value);
  }

  searchBrokers(searchValue: string): void {
    const pagedRequestDTO: PagedRequestDTO = {
      entityClass: 'TblBroker',
      pageSize: 10,
      pageNumber: 0,
      sortField: 'name',
      sortDirection: 'ASC',
      fieldMap: {},
    };

    const fields = [
      { key: 'name', name: 'name', logicGate: 'AND', bracket: '(' },
      {
        key: 'brokerNumber',
        name: 'brokerNumber',
        logicGate: 'OR',
        bracket: ')',
      },
    ];

    fields.forEach((field) => {
      pagedRequestDTO.fieldMap[field.key] = {
        name: field.name,
        value: searchValue,
        logicGate: field.logicGate,
        bracket: field.bracket || '',
      };
    });

    this.brokerService
      .getListOfBrokers(pagedRequestDTO)
      .pipe(take(1))
      .subscribe({
        next: (response: PagedResponseDTO<BrokerDTO>) => {
          this.filteredBrokers = response.resultList;
        },
        error: (error) => console.error('Error fetching brokers:', error),
      });
  }
}
